/**
 * Shared CSM company detail panel: token-consumption chart + user breakdown for
 * one platform (prod) company. Opened from the Customers roster, for both
 * Attio-linked and unlinked platform companies.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Badge } from '../../../components/ui/Badge';
import { InlineStat } from '../../../components/ui/Card';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useCsmAosCompanyDetail, useCsmCompanyUsers } from '../../../hooks/useDashboard';
import { mergeDailyTokens, needsSplitScale, type DailyTokens } from './tokenSeries';
import type { CsmCompany, CsmUser } from '../../../api/dashboard';
import { formatDayMonth, formatDayMonthTime } from '../../../utils/dateFormat';
import { formatCompact, formatNumber } from '../../../utils/numberFormat';
import { CHART_AXIS, CHART_GRID, CHART_HEIGHT, CHART_LEGEND, CHART_SERIES, CHART_TOOLTIP, ChartEmpty } from '../../../components/ui/chart';

function lastSeen(value: string | null, never = 'Never'): string {
  return value ? formatDayMonthTime(value) : never;
}

function formatTokenDay(value: string): string {
  return formatDayMonth(`${value}T00:00:00Z`);
}

/** The computed CS health score (daily CSM snapshot), never blank: an unscored company says so. */
export function healthStat(c: Pick<CsmCompany, 'healthScore' | 'healthTier' | 'healthUsageOnly' | 'healthSnapshotDate' | 'healthDeclinePenalty'>): {
  value: string;
  sub: string;
} {
  if (c.healthScore == null) return { value: 'Not scored yet', sub: 'no daily CSM snapshot has scored it' };
  const parts = [c.healthSnapshotDate ? `scored ${formatTokenDay(c.healthSnapshotDate)}` : 'scored'];
  if (c.healthDeclinePenalty) parts.push(`−${c.healthDeclinePenalty} decline penalty`);
  if (c.healthUsageOnly) parts.push('usage only');
  return { value: c.healthTier ? `${c.healthScore} · ${c.healthTier}` : String(c.healthScore), sub: parts.join(' · ') };
}

/** External and Internal tables share these widths so their columns line up. */
const USER_TABLE_COLS = ['auto', '9rem', '7rem', '7rem', '10rem'];

/** A long company (59 users) opens on its 10 most active; the rest sit behind one button. */
const USER_PREVIEW_ROWS = 10;

function UserTable({ title, users, empty }: { title: string; users: CsmUser[]; empty: string }) {
  const [showAll, setShowAll] = useState(false);
  const ranked = useMemo(
    () =>
      [...users].sort(
        (a, b) =>
          b.loginCount30d - a.loginCount30d ||
          b.loginCount90d - a.loginCount90d ||
          (b.lastLoginAt ?? '').localeCompare(a.lastLoginAt ?? ''),
      ),
    [users],
  );
  const shown = showAll ? ranked : ranked.slice(0, USER_PREVIEW_ROWS);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h5 className="text-body-sm font-medium text-text-secondary">{title}</h5>
        <Badge variant="pill">{users.length}</Badge>
      </div>
      {users.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">{empty}</p>
      ) : (
        <Table minWidth={680} colWidths={USER_TABLE_COLS}>
          <THead>
            <tr className="border-b border-border-light">
              <Th>User</Th>
              <Th>Role</Th>
              <Th align="right">Events 30d</Th>
              <Th align="right">Events 90d</Th>
              <Th align="right" className="!pr-3">Last active</Th>
            </tr>
          </THead>
          <tbody>
            {shown.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <p className="flex items-center gap-2 font-medium text-text-primary">
                    {user.name}
                    {/* Active/inactive is already in Events 30d; only the exception gets a pill. */}
                    {user.status === 'unmapped' && <Badge>unmapped</Badge>}
                  </p>
                  <p className="text-body-xs text-text-tertiary" title={user.auth0UserId ?? 'No Auth0 ID'}>
                    {user.email ?? 'No email'}
                  </p>
                </Td>
                <Td className="text-text-secondary">{user.role ?? <Nil />}</Td>
                <Td align="right" className="tabular-nums">{formatNumber(user.loginCount30d)}</Td>
                <Td align="right" className="tabular-nums">{formatNumber(user.loginCount90d)}</Td>
                <Td align="right" className="whitespace-nowrap text-text-secondary !pr-3">
                  {user.lastLoginAt ? lastSeen(user.lastLoginAt) : <Nil />}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {users.length > USER_PREVIEW_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-body-xs font-medium text-accent hover:underline"
        >
          {showAll ? `Show the top ${USER_PREVIEW_ROWS}` : `Show all ${users.length} (most active first)`}
        </button>
      )}
    </div>
  );
}

/** A section heading inside an expanded row: the row is already in a card, so its parts are not cards. */
function SectionTitle({ children, info }: { children: string; info?: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <h4 className="text-body-sm font-medium text-text-primary">{children}</h4>
      {info && <InfoTip>{info}</InfoTip>}
    </div>
  );
}

const SERIES = [
  { key: 'classic', name: 'Classic', color: CHART_SERIES[0] },
  { key: 'aos', name: 'aOS', color: CHART_SERIES[2] },
] as const;

function tokenAxes() {
  return [
    <CartesianGrid key="g" {...CHART_GRID} />,
    <XAxis key="x" dataKey="date" tickFormatter={formatTokenDay} {...CHART_AXIS} minTickGap={24} />,
    <YAxis key="y" tickFormatter={(value) => formatCompact(Number(value))} {...CHART_AXIS} width={56} />,
    <Tooltip
      key="t"
      {...CHART_TOOLTIP}
      formatter={(value, name) => [formatNumber(Number(value ?? 0)), String(name)]}
      labelFormatter={(label) => formatTokenDay(String(label))}
    />,
  ];
}

function tokenArea(s: (typeof SERIES)[number], stackId?: string) {
  return (
    <Area
      key={s.key}
      type="linear"
      dataKey={s.key}
      name={s.name}
      stackId={stackId}
      stroke={s.color}
      strokeWidth={2}
      fill={s.color}
      fillOpacity={0.12}
      dot={false}
      activeDot={{ r: 4 }}
      isAnimationActive={false}
    />
  );
}

/**
 * Stacked when the two platforms are comparable; two small charts, each on its
 * own axis and titled with its total, when one dwarfs the other.
 */
function TokenChart({ daily, split }: { daily: DailyTokens[]; split: boolean }) {
  if (!split) {
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
        <AreaChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {tokenAxes()}
          <Legend {...CHART_LEGEND} formatter={(value) => <span className="text-text-secondary">{value}</span>} />
          {SERIES.map((s) => tokenArea(s, 'tokens'))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {SERIES.map((s) => {
        const total = daily.reduce((n, d) => n + d[s.key], 0);
        return (
          <div key={s.key} className="space-y-1">
            <p className="flex items-center gap-1.5 text-body-xs text-text-secondary">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
              <span className="font-medium text-text-primary">{s.name}</span> · {formatCompact(total) || '0'} · own scale
            </p>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
              <AreaChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                {tokenAxes()}
                {tokenArea(s)}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}

export function CompanyUsersRow({
  companyId,
  activityWindow,
  periodText,
  leadTokens,
}: {
  companyId: string;
  activityWindow: { from: string; to: string };
  periodText: string;
  /** The roster row's Classic + aOS figure: when given, the panel leads with it, as the row does. */
  leadTokens?: { total: number; aos: number };
}) {
  const { data, isLoading, error, refetch } = useCsmCompanyUsers(companyId, activityWindow, true);
  // The chart is Classic + aOS, like the roster's token figure: an aOS-only company (GELITA) otherwise charts nothing.
  const aos = useCsmAosCompanyDetail(companyId, activityWindow, true);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Couldn’t load company details" error={error} onRetry={() => refetch()} />;
  if (!data) {
    return <EmptyState title="No company details found" description="This company is not in the production mirror." compact />;
  }

  const daily = mergeDailyTokens(data.tokenHistory, aos.data?.series ?? null);
  const hasTokenActivity = daily.some((d) => d.classic > 0 || d.aos > 0);
  // One side many times the other flattens it on a shared axis: draw each on its own.
  const split = needsSplitScale(daily);
  const aosFailed = !!aos.error;
  const externalUsers = data.users.filter((user) => user.userType === 'external');
  const internalUsers = data.users.filter((user) => user.userType === 'internal');
  const c = data.company;
  const health = healthStat(c);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {leadTokens ? (
          <InlineStat
            label={`Tokens, ${periodText}`}
            value={formatCompact(leadTokens.total) || '0'}
            sub={`${formatCompact(leadTokens.total - leadTokens.aos) || '0'} Classic + ${formatCompact(leadTokens.aos) || '0'} aOS · customer users`}
            size="sm"
          />
        ) : (
          <InlineStat
            label={`Classic tokens, ${periodText}`}
            value={formatCompact(data.tokenSummary.totalTokens30d) || '0'}
            sub={data.tokenSummary.lastTokenAt ? `last ${lastSeen(data.tokenSummary.lastTokenAt)}` : 'never'}
            size="sm"
          />
        )}
        <InlineStat
          label="Active external users"
          value={`${formatNumber(c.activeUsers30d)} / ${formatNumber(c.mappedUserCount)}`}
          sub={c.unmappedUserCount > 0 ? `${formatNumber(c.unmappedUserCount)} unmapped` : undefined}
          size="sm"
        />
        <InlineStat label="Last external activity" value={lastSeen(c.lastLoginAt)} size="sm" />
        <InlineStat label="CS health score (0–100)" value={health.value} sub={health.sub} size="sm" />
        <InlineStat
          label="Internal team"
          value={formatNumber(c.internalMemberCount)}
          sub={`${formatNumber(c.internalActiveUsers30d)} active · ${
            c.lastInternalLoginAt ? `last ${lastSeen(c.lastInternalLoginAt)}` : 'never active'
          }`}
          size="sm"
        />
      </div>

      <div className="space-y-2">
        <SectionTitle
          info={
            <>
              Classic tokens plus aOS tokens by customer users (SEQUESTO staff left out of aOS), stacked: the same two
              sources as the roster&rsquo;s token figure. When one platform has more than 5× the other&rsquo;s tokens,
              each gets its own small chart and scale so the smaller one stays readable. The aOS breakdown by user and project is on Usage › aOS.
            </>
          }
        >
          {split ? 'Daily tokens, Classic and aOS on separate scales' : 'Daily tokens, Classic + aOS'}
        </SectionTitle>
        {aosFailed && (
          <p className="text-body-xs text-warning-dark">aOS tokens couldn’t load, so the chart shows Classic only.</p>
        )}
        {aos.isLoading ? (
          <LoadingState height={CHART_HEIGHT.small} />
        ) : !hasTokenActivity ? (
          <ChartEmpty label={`No Classic or aOS token activity in the ${periodText}.`} height={CHART_HEIGHT.small} />
        ) : (
          <TokenChart daily={daily} split={split} />
        )}
      </div>

      {data.users.length === 0 ? (
        <EmptyState title="No members found" description="This company has no synced members in the production mirror." compact />
      ) : (
        <div className="space-y-4 border-t border-border-light pt-4">
          <SectionTitle
            info={
              <>
                External users drive customer health: one successful Auth0 event in the {periodText} makes a mapped
                user active. Internal @sequesto.com members are shown for context and left out of every KPI.
              </>
            }
          >
            Users
          </SectionTitle>
          <UserTable title="External" users={externalUsers} empty="No external members are mapped to this company." />
          {internalUsers.length > 0 && (
            <UserTable title="Internal" users={internalUsers} empty="No Sequesto team members on this company." />
          )}
        </div>
      )}
    </div>
  );
}
